import type {
  MarkdownValueFormat,
} from '../features/ai-visualization/v2/modules/content/markdown-template.ts';
import katex from 'katex';
import rehypeKatex from 'rehype-katex';
import rehypeStringify from 'rehype-stringify';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import { unified } from 'unified';
import {
  analyzeMarkdownTemplateText,
  isMarkdownFormat,
  readMarkdownSelector,
} from '../features/ai-visualization/v2/modules/content/markdown-template.ts';
import 'katex/dist/katex.min.css';

interface BoundNode { type: string; value?: string; tagName?: string; properties?: Record<string, unknown>; children?: BoundNode[] }

export interface MarkdownBindingDiagnostic {
  code: string;
  name?: string;
  expression?: string;
  message: string;
}

export interface MarkdownBindingObservation {
  name: string;
  status: 'rendered' | 'empty' | 'uninitialized' | 'unbound' | 'missing-path' | 'invalid-format' | 'type-mismatch' | 'non-finite';
  renderedText: string;
  format: MarkdownValueFormat;
}

export interface MarkdownRenderResult {
  html: string;
  bindings: MarkdownBindingObservation[];
  diagnostics: MarkdownBindingDiagnostic[];
  unresolvedExpressions: string[];
}

interface MarkdownRenderOptions {
  boundInputs?: readonly string[];
}

function normalizeNegativeZero(value: string): string {
  const numeric = Number(value.replace(/%$/, ''));
  return Object.is(numeric, -0) || numeric === 0 ? value.replace(/^-/, '') : value;
}

function displayDefault(value: unknown): string {
  if (Array.isArray(value))
    return value.join('、');
  if (value === null)
    return '';
  if (value === undefined)
    return '—';
  return String(value);
}

function formatBinding(
  name: string,
  value: unknown,
  connected: boolean,
  requestedFormat: unknown,
  missingPath = false,
): { observation: MarkdownBindingObservation; diagnostics: MarkdownBindingDiagnostic[] } {
  const diagnostics: MarkdownBindingDiagnostic[] = [];
  const fallback: MarkdownValueFormat = { style: 'default' };
  const format = requestedFormat === undefined
    ? fallback
    : isMarkdownFormat(requestedFormat) ? requestedFormat : fallback;
  if (requestedFormat !== undefined && !isMarkdownFormat(requestedFormat)) {
    diagnostics.push({
      code: 'markdown.format_invalid',
      name,
      message: `formats.${name} 不是受支持的显示格式。`,
    });
    return {
      observation: { name, status: 'invalid-format', renderedText: '⚠ 格式无效', format },
      diagnostics,
    };
  }
  if (!connected) {
    diagnostics.push({ code: 'markdown.binding_unbound', name, message: `动态变量 ${name} 没有连接到公共通道。` });
    return { observation: { name, status: 'unbound', renderedText: '⚠ 未绑定', format }, diagnostics };
  }
  if (missingPath) {
    diagnostics.push({ code: 'markdown.binding_path_missing', name, message: `公共对象中不存在可读取的字段 ${name}；请核对实际字段，缺失值不是零。` });
    return { observation: { name, status: 'missing-path', renderedText: '⚠ 字段缺失', format }, diagnostics };
  }
  if (value === undefined) {
    return { observation: { name, status: 'uninitialized', renderedText: '—', format }, diagnostics };
  }
  if (value === null) {
    // Undefined estimates are not zero; numeric summaries should show a clear
    // placeholder instead of an apparently broken interval such as [, ].
    return { observation: { name, status: 'empty', renderedText: format.style === 'default' ? '' : '—', format }, diagnostics };
  }
  if (typeof value === 'number' && !Number.isFinite(value)) {
    diagnostics.push({ code: 'markdown.value_non_finite', name, message: `动态变量 ${name} 不是有限数值。` });
    return { observation: { name, status: 'non-finite', renderedText: '—', format }, diagnostics };
  }
  if (format.style === 'default') {
    return { observation: { name, status: 'rendered', renderedText: displayDefault(value), format }, diagnostics };
  }
  if (typeof value !== 'number') {
    diagnostics.push({ code: 'markdown.format_type_mismatch', name, message: `formats.${name} 只能格式化数值，实际收到 ${Array.isArray(value) ? '数组' : typeof value}。` });
    return { observation: { name, status: 'type-mismatch', renderedText: '⚠ 类型不符', format }, diagnostics };
  }
  const digits = format.digits ?? 0;
  const renderedText = format.style === 'scientific'
    ? value.toExponential(digits)
    : format.style === 'percent'
      ? `${normalizeNegativeZero((value * 100).toFixed(digits))}%`
      : normalizeNegativeZero(value.toFixed(digits));
  return { observation: { name, status: 'rendered', renderedText, format }, diagnostics };
}

export function renderMarkdown(
  content: string,
  bindings: Record<string, unknown> = {},
  formats: Record<string, unknown> = {},
  options: MarkdownRenderOptions = {},
): MarkdownRenderResult {
  const observations = new Map<string, MarkdownBindingObservation>();
  const diagnostics: MarkdownBindingDiagnostic[] = [];
  const unresolvedExpressions = new Set<string>();
  const connected = options.boundInputs ? new Set(options.boundInputs) : null;
  const processor = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkMath)
    .use(remarkRehype)
    .use(() => (tree: BoundNode) => {
      const replace = (node: BoundNode, math = false, literal = false): void => {
        math ||= JSON.stringify(node.properties?.className ?? '').includes('math-');
        literal ||= node.tagName === 'code' || node.tagName === 'pre';
        if (!node.children)
          return;
        node.children = node.children.flatMap((child) => {
          if (child.type !== 'text' || !child.value || literal) {
            replace(child, math, literal);
            return [child];
          }
          const analysis = analyzeMarkdownTemplateText(child.value);
          for (const item of analysis.issues) {
            diagnostics.push({ code: item.code, expression: item.expression, message: item.message });
            unresolvedExpressions.add(item.expression);
          }
          const replacements = [
            ...analysis.tokens.map(token => ({ ...token, kind: 'binding' as const })),
            ...analysis.issues.map(issue => ({ ...issue, kind: 'issue' as const })),
          ].sort((left, right) => left.start - right.start);
          if (replacements.length === 0)
            return [child];
          const parts: BoundNode[] = [];
          let cursor = 0;
          for (const item of replacements) {
            parts.push({ type: 'text', value: child.value.slice(cursor, item.start) });
            if (item.kind === 'binding') {
              const root = item.name.split('.')[0]!;
              const selected = readMarkdownSelector(bindings, item.name);
              const result = formatBinding(
                item.name,
                selected.value,
                connected ? connected.has(root) : Object.hasOwn(bindings, root),
                formats[item.name],
                selected.missingPath,
              );
              observations.set(item.name, result.observation);
              diagnostics.push(...result.diagnostics);
              const value = result.observation.renderedText;
              parts.push(math
                ? { type: 'text', value }
                : {
                    type: 'element',
                    tagName: 'span',
                    properties: {
                      'data-wire-value': item.name,
                      'data-wire-status': result.observation.status,
                      'data-wire-type': typeof selected.value,
                    },
                    children: [{ type: 'text', value }],
                  });
            }
            else {
              parts.push({
                type: 'element',
                tagName: 'span',
                properties: { 'data-template-error': item.code },
                children: [{ type: 'text', value: '⚠ 不支持的占位符' }],
              });
            }
            cursor = item.end;
          }
          parts.push({ type: 'text', value: child.value.slice(cursor) });
          return parts;
        });
      };
      replace(tree);
    })
    .use(rehypeKatex)
    .use(rehypeStringify);
  const html = processor.processSync(content).toString();
  for (const name of Object.keys(formats)) {
    if (!observations.has(name)) {
      diagnostics.push({
        code: 'markdown.format_unused',
        name,
        message: `formats.${name} 没有对应的动态占位符。`,
      });
    }
  }
  return {
    html,
    bindings: [...observations.values()],
    diagnostics,
    unresolvedExpressions: [...unresolvedExpressions],
  };
}

function toMarkdown(content: string, bindings?: Record<string, string>): string {
  return renderMarkdown(content, bindings ?? {}).html;
}

function renderLatex(text: string) {
  try {
    // 查找文本中的 LaTeX 代码（用 \( 和 \) 包裹）
    const latexRegex = /\\\((.*?)\\\)/g;
    return text.replace(latexRegex, (match, latex) => {
      // 使用 katex 渲染 LaTeX 代码
      return katex.renderToString(latex, { throwOnError: false });
    });
  }
  catch (error) {
    console.error('LaTeX 渲染出错:', error);
    return text;
  }
}

export {
  renderLatex,
  toMarkdown,
};
